import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { EMPTY, Observable, Subject, Subscription, throwError } from "rxjs";
import { environment } from "../../environments/environment";
import { UserRoles } from "../models/enums";
import {
  AccountInfo,
  EventMessage,
  EventType,
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
  AuthError,
  InteractionRequiredAuthError,
  SilentRequest
} from "@azure/msal-browser";
import { catchError, filter, map, takeUntil } from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import { userReadWriteGroups } from "../../assets/user-roles";

/** Service to handle authorization and authentication */
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private isLoggedIn = false;
  public loggedInStatusChanged = new Subject<boolean>();
  private accessToken!: string;
  public accessTokenChanged = new Subject<string>();
  private readonly _destroying$ = new Subject<void>();
  private tokenListenersSubscription = Subscription.EMPTY;
  private userRoles!: UserRoles[] | null;
  private authRedirectInProgress = false;
  private loadedAccountId: string | null = null;
  private loadingUserProfile = false;

  baseGraphUrl = "https://graph.microsoft.com/v1.0/";
  graphUser: any;
  graphUserGroups: any;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private router: Router,
    private msalBroadcastService: MsalBroadcastService,
    private httpClient: HttpClient,
  ) {
    this.setupLoginListeners();
  }

  setupLoginListeners() {
    this.msalService.handleRedirectObservable().pipe(
        takeUntil(this._destroying$)
    ).subscribe(
        (resp) => {
          if (resp?.account) {
            this.msalService.instance.setActiveAccount(resp.account);
          }
          this.authRedirectInProgress = false;
          this.checkAndSetActiveAccount();
          this.updateLoggedInStatus(this.anyAccounts());
        }
    );

    this.msalBroadcastService.msalSubject$.pipe(
        filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
    ).subscribe(eventMsg => {
        console.log('login failure ' + JSON.stringify(eventMsg), eventMsg);
        this.authRedirectInProgress = false;
        this.updateLoggedInStatus(false);
    });

    this.msalBroadcastService.msalSubject$.pipe(
        filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
    ).subscribe(eventMsg => {
        this.authRedirectInProgress = false;
        const authResult = eventMsg.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(authResult.account);
        this.updateLoggedInStatus(true);
    });

    this.msalBroadcastService.msalSubject$.pipe(
        filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$)
    ).subscribe((eventMsg) => {
        this.authRedirectInProgress = false;
        this.updateLoggedInStatus(true);
    });
  }


  //#region Public
  public getIsLoggedIn() {
    return this.isLoggedIn;
  }

  public subscribeTokenListeners(): void {
    if (!this.tokenListenersSubscription.closed) {
      return;
    }

    this.tokenListenersSubscription = new Subscription();

    // To sync when adding/removing accounts across tabs see
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/events.md#syncing-logged-in-state-across-tabs-and-windows
    this.tokenListenersSubscription.add(
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS)
        )
        .subscribe((result: EventMessage) => {
          if (result?.payload) {
            const payload: AuthenticationResult = result.payload as AuthenticationResult;
            this.msalService.instance.setActiveAccount(payload.account);
            this.updateAccessToken(payload.accessToken);
          }
        })
    );

    this.tokenListenersSubscription.add(
      this.msalBroadcastService.inProgress$
        .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
        .subscribe(() => {
          this.checkAndSetActiveAccount();
          this.updateLoggedInStatus(this.anyAccounts());
        })
    );

    this.tokenListenersSubscription.add(
      this.msalBroadcastService.msalSubject$
        .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE))
        .subscribe(() => {
          this.updateLoggedInStatus(false);
          this.router.navigateByUrl("/not-authorized");
        })
    );

    this.updateLoggedInStatus(this.anyAccounts());
  }

  public unsubscribeTokenListeners(): void {
    this.tokenListenersSubscription.unsubscribe();
    this.tokenListenersSubscription = Subscription.EMPTY;
  }

  private checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public getApiAccessToken(scope: string[]): Observable<string> {
    if (!this.msalService.instance.getActiveAccount()) {
      this.checkAndSetActiveAccount();
    }
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      return EMPTY;
    }

    const observable = this.msalService.acquireTokenSilent({
      account: activeAccount,
      scopes: [...scope]
    } as SilentRequest);
    return observable.pipe(
        map((result: AuthenticationResult) => result.accessToken),
        catchError((error: unknown) => {
          if (this.isInteractiveAuthError(error)) {
            this.startInteractiveLogin(scope);
            return EMPTY;
          }

          return throwError(() => error);
        })
    );
  }

  public getHighestLevelUserRole(): UserRoles {
    if (!Array.isArray(this.userRoles) || this.userRoles.length < 1) {
      return UserRoles.None;
    }

    if (this.userRoles.includes(UserRoles.Write)) return UserRoles.Write;
    if (this.userRoles.includes(UserRoles.Read)) return UserRoles.Read;

    return UserRoles.None;
  }

  public userHasMinimumRole(minRole: UserRoles): boolean {
    // All users meet this requirement because this requirement has no required roles
    if (minRole === UserRoles.None) return true;

    // If user does not have any roles
    if (!Array.isArray(this.userRoles) || this.userRoles.length < 1) {
      return false;
    }

    // If user has role
    if (this.userRoles.includes(minRole)) return true;

    return false;
  }

  public login(): void {
    this.startInteractiveLogin();
  }

  public logout(): void {
    this.authRedirectInProgress = false;
    this.updateLoggedInStatus(false);
    this.msalService.logoutRedirect();
  }

  public getUserName(): string {
    return this.msalService.instance.getActiveAccount()?.name ?? "";
  }
  //#endregion

  //#region Private
  private updateLoggedInStatus(newStatus: boolean): void {
    if (newStatus && !this.isAllowedAccount(this.msalService.instance.getActiveAccount())) {
      newStatus = false;
    }

    if (this.isLoggedIn !== newStatus) {
      this.isLoggedIn = newStatus;
      this.loggedInStatusChanged.next(this.isLoggedIn);
    }
    this.updateUserRoles();
  }

  private updateAccessToken(newToken: string): void {
    if (this.accessToken !== newToken) {
      this.accessToken = newToken;
      this.accessTokenChanged.next(this.accessToken);
    }
  }

  private updateUserRoles(): void {
    if (!this.isLoggedIn) {
      this.userRoles = null;
      this.graphUser = null;
      this.graphUserGroups = null;
      this.loadedAccountId = null;
      this.loadingUserProfile = false;
      return;
    }

    const activeAccountId = this.getActiveAccountId();
    if (!activeAccountId || this.loadingUserProfile) {
      return;
    }

    if (this.loadedAccountId === activeAccountId && this.userRoles?.length) {
      return;
    }

    this.getGraphUser();
  }

  private getGraphUser() {
    this.loadingUserProfile = true;

    this.httpClient.get(this.baseGraphUrl + "me").subscribe({
      next: (data) => {
        this.graphUser = data;
        this.fetchGraphUserGroups();
      },
      error: (error) => {
        this.loadingUserProfile = false;
        console.error(
          " Http get request to MS Graph failed" + JSON.stringify(error)
        );
      }
    });
  }

  private handleError(error: any) {
    this.loadingUserProfile = false;
    console.error(error);
  }

  private isInteractiveAuthError(error: unknown): boolean {
    return (error instanceof InteractionRequiredAuthError || error instanceof AuthError) &&
      !!error.errorCode &&
      (
        error.errorCode === "consent_required" ||
        error.errorCode === "interaction_required" ||
        error.errorCode === "login_required" ||
        error.errorCode === "monitor_window_timeout" ||
        error.errorCode === "token_renewal_error"
      );
  }

  private startInteractiveLogin(scopes?: string[]): void {
    if (this.authRedirectInProgress) {
      return;
    }

    this.authRedirectInProgress = true;
    const authRequest = this.msalGuardConfig.authRequest ?? {};

    if (scopes && scopes.length > 0) {
      this.msalService.acquireTokenRedirect({
        ...authRequest,
        scopes: [...scopes]
      } as RedirectRequest);
    } else if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  private isAllowedAccount(user: AccountInfo | null | undefined): boolean {
    if (!user) {
      return false;
    }

    const tokenTenant = user.idTokenClaims?.["tid"];
    const tokenClient = user.idTokenClaims?.["aud"];

    return !!tokenTenant &&
      !!tokenClient &&
      tokenTenant === environment.config.tenantID &&
      tokenClient === environment.config.clientID;
  }

  private hasWriteGroups() {
    const groups = userReadWriteGroups.DLI;
    if (this.graphUserGroups && this.graphUserGroups.value) {
      return this.graphUserGroups.value.findIndex(x => {
          return groups.includes(x.id);
        }) >=
        0;
    } else {
      return false;
    }
  }

  private fetchGraphUserGroups() {
    this.httpClient
      .get(`${this.baseGraphUrl}/users/${this.graphUser.id}/memberOf`)
      .subscribe({
        next: this.fetchGraphUserGroupsHandler.bind(this),
        error: this.handleError.bind(this)
      });
  }

  private fetchGraphUserGroupsHandler(response: any) {
    this.graphUserGroups = response;

    const rolesEnumArr: UserRoles[] = [];
    if (this.hasWriteGroups()) {
      rolesEnumArr.push(UserRoles.Read, UserRoles.Write);
    } else {
      rolesEnumArr.push(UserRoles.Read);
    }

    this.userRoles = rolesEnumArr;
    this.loadedAccountId = this.getActiveAccountId();
    this.loadingUserProfile = false;
  }

  getActiveAccount() {
    return this.msalService.instance.getActiveAccount();
  }

  private anyAccounts(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  private getActiveAccountId(): string | null {
    return this.getActiveAccount()?.homeAccountId ?? null;
  }
}
